/**
 * Global command palette (⌘K / Ctrl+K) — a Spotlight/Linear-style launcher for
 * the hotel-scoped navigation. Open state + query live in the shared
 * `@hostesa/state` commandPalette slice; the list is filtered by the active
 * role's permissions + feature flags (same gate as the sidebar). cmdk handles
 * fuzzy matching + ↑/↓/↵ keyboard nav; the Dialog provides the scale/fade
 * animation and Esc-to-close.
 */
import { useAuth } from '@hostesa/auth-client';
import { useFlags } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import {
  closeCommandPalette,
  openCommandPalette,
  selectCommandPaletteOpen,
  selectCommandPaletteQuery,
  setCommandPaletteQuery,
  toggleCommandPalette,
  useAppDispatch,
  useAppSelector,
} from '@hostesa/state';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@hostesa/ui';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useEffect } from 'react';
import { useHotelContext } from '../lib/hotel-context';
import { NAV_GROUPS } from './nav-config';

/** A compact top-bar button that opens the palette and advertises the ⌘K shortcut. */
export function CommandPaletteTrigger() {
  const { t } = useT('platform');
  const dispatch = useAppDispatch();
  return (
    <button
      type="button"
      data-testid="command-palette-trigger"
      onClick={() => dispatch(openCommandPalette())}
      className="flex h-9 items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-alabaster)] px-3 text-sm text-[var(--color-stone-mid)] transition-colors hover:border-[var(--color-stone-300)] hover:text-[var(--color-charcoal)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] sm:w-64 lg:w-80"
    >
      <Search className="size-4 shrink-0" strokeWidth={1.3} aria-hidden="true" />
      <span className="hidden sm:inline">{t('commandPalette.triggerLabel')}</span>
      <kbd className="ml-auto hidden rounded bg-[var(--color-canvas)] px-1.5 py-0.5 font-[var(--font-mono)] text-[10px] text-[var(--color-stone-mid)] sm:inline">
        ⌘K
      </kbd>
    </button>
  );
}

/** The palette overlay. Mount once inside the hotel shell. */
export function CommandPalette() {
  const { t } = useT('platform');
  const { can } = useAuth();
  const flags = useFlags();
  const hotel = useHotelContext();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectCommandPaletteOpen);
  const query = useAppSelector(selectCommandPaletteQuery);

  const base = `/${hotel.tenantSlug}/${hotel.hotelSlug}`;

  // ⌘K / Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        dispatch(toggleCommandPalette());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dispatch]);

  const go = (segment: string) => {
    dispatch(closeCommandPalette());
    void navigate({ to: `${base}/${segment}` });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dispatch(closeCommandPalette());
      }}
    >
      <DialogContent
        hideClose
        data-testid="command-palette"
        className="top-[12vh] max-w-xl translate-y-0 overflow-hidden p-0 shadow-lg"
      >
        <DialogTitle className="sr-only">{t('commandPalette.title')}</DialogTitle>
        <Command>
          <CommandInput
            value={query}
            onValueChange={(v) => dispatch(setCommandPaletteQuery(v))}
            placeholder={t('commandPalette.placeholder')}
          />
          <CommandList>
            <CommandEmpty>{t('commandPalette.empty')}</CommandEmpty>
            {NAV_GROUPS.map((group) => {
              const visible = group.items
                .filter((item) => can(item.permission))
                .filter((item) => (item.flag ? flags[item.flag] : true));
              if (visible.length === 0) return null;
              return (
                <CommandGroup key={group.id} heading={t(group.labelKey)}>
                  {visible.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={`${t(group.labelKey)} ${t(item.labelKey)}`}
                      data-testid={`command-item-${item.id}`}
                      onSelect={() => go(item.segment)}
                    >
                      {t(item.labelKey)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
